import React, {useState, useEffect, useRef, useContext} from 'react';
import {FiChevronDown, FiChevronRight} from 'react-icons/fi';
import SettingsShell from '../../templates/SettingsShell';
import ProfilePage from './ProfilePage';
import Preferences from './Preferences';
import Notifications from './Notifications';
import Archived from './ArchivedTable';
import Accounts from './Accounts';
import Resources from './Tables/Resources';
import VendorTypes from './Tables/VendorTypes';
import FeedTypes from './Tables/FeedTypes';
import FileDetails from './Tables/FileDetails';
import Agreement from './Agreement';
import Roster from './Roster';
import {Element} from 'react-scroll';
import {UserContext} from '../../context/UserProvider';
import gsap from 'gsap';

export default function Settings() {
  const {role} = useContext(UserContext);

  const [expandedSections, setExpandedSections] = useState({
    profile: false,
    preferences: false,
    notifications: false,
    accounts: false,
    archived: false,
    resources: false,
    vendortypes: false,
    feedtypes: false,
    filedetails: false,
    tos: false,
    roster: false,
  });
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('none');

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setMaxHeight(`${contentHeight}px`);
      console.log(maxHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentRef]);

  const toggleAllSections = () => {
    const newState = isAllExpanded
      ? {}
      : {
        profile: true,
        preferences: true,
        notifications: true,
        accounts: true,
        archived: true,
        resources: true,
        vendortypes: true,
        feedtypes: true,
        filedetails: true,
        tos: true,
        roster: true,
      };
    setExpandedSections(newState);
    setIsAllExpanded(!isAllExpanded);
  };

  const toggleFromShell = (sectionName) => {
    if (!expandedSections[sectionName]) {
      setExpandedSections({
        ...expandedSections,
        [sectionName]: true,
      });
    }
  };

  const toggleSection = (sectionName) => {
    setExpandedSections({
      ...expandedSections,
      [sectionName]: !expandedSections[sectionName],
    });
  };

  const refs = {
    profile: useRef(null),
    preferences: useRef(null),
    notifications: useRef(null),
    accounts: useRef(null),
    archived: useRef(null),
    resources: useRef(null),
    vendortypes: useRef(null),
    feedtypes: useRef(null),
    filedetails: useRef(null),
    tos: useRef(null),
    roster: useRef(null),
  };

  useEffect(() => {
    Object.keys(expandedSections).forEach((section) => {
      if (refs[section]?.current) {
        gsap.to(refs[section].current, {
          maxHeight: expandedSections[section] ? 1700 : 0,
          duration: 0.3,
          ease: 'power1.out',
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedSections]);

  return (
    <div>
      <SettingsShell
        toggleFromShell={toggleFromShell}
        toggleAllSections={toggleAllSections}
        isAllExpanded={isAllExpanded}
      >
        <Element name="profile">
          <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
            <h2
              onClick={() => toggleSection('profile')}
              id="profile"
              className="w-full select-none pb-3 pl-12 pt-4 text-xl font-semibold text-gray-800 dark:text-gray-200"
            >
              <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                {expandedSections.profile ? (
                  <FiChevronDown size={16}/>
                ) : (
                  <FiChevronRight size={16}/>
                )}
              </button>
              Profile Information
            </h2>
            <div
              ref={refs.profile}
              style={{maxHeight: expandedSections.profile ? '800px' : '0px'}}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.profile ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
            >
              <ProfilePage/>
            </div>
          </div>
        </Element>
        <Element name="preferences">
          <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
            <h2
              onClick={() => toggleSection('preferences')}
              id="preferences"
              className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
            >
              <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                {expandedSections.preferences ? (
                  <FiChevronDown size={16}/>
                ) : (
                  <FiChevronRight size={16}/>
                )}
              </button>
              User Preferences
            </h2>
            <div
              ref={refs.preferences}
              style={{
                maxHeight: expandedSections.preferences ? '800px' : '0px',
              }}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.preferences ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
            >
              <Preferences/>
            </div>
          </div>
        </Element>
        <Element name="notifications">
          <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
            <h2
              onClick={() => toggleSection('notifications')}
              id="notifications"
              className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
            >
              <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                {expandedSections.notifications ? (
                  <FiChevronDown size={16}/>
                ) : (
                  <FiChevronRight size={16}/>
                )}
              </button>
              Notification Settings
            </h2>
            <div
              ref={refs.notifications}
              style={{
                maxHeight: expandedSections.notifications ? '800px' : '0px',
              }}
              className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.notifications ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
            >
              <Notifications/>
            </div>
          </div>
        </Element>
        {role === 'Admin' && (
          <>
            <Element name="accounts">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('accounts')}
                  id="accounts"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.accounts ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  User Accounts
                </h2>
                <div
                  ref={refs.accounts}
                  style={{
                    maxHeight: expandedSections.accounts ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.accounts ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <Accounts/>
                </div>
              </div>
            </Element>
            <Element name="archived">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('archived')}
                  id="archived"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.archived ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Archived Vendors
                </h2>
                <div
                  ref={refs.archived}
                  style={{
                    maxHeight: expandedSections.archived ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.archived ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <Archived/>
                </div>
              </div>
            </Element>
            <Element name="resources">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('resources')}
                  id="resources"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.resources ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Portal Resources
                </h2>
                <div
                  ref={refs.resources}
                  style={{
                    maxHeight: expandedSections.resources ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.resources ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <Resources/>
                </div>
              </div>
            </Element>
            <Element name="vendortypes">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('vendortypes')}
                  id="vendortypes"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.vendortypes ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Vendor Types
                </h2>
                <div
                  ref={refs.vendortypes}
                  style={{
                    maxHeight: expandedSections.vendortypes ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.vendortypes ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <VendorTypes/>
                </div>
              </div>
            </Element>
            <Element name="feedtypes">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('feedtypes')}
                  id="feedtypes"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.feedtypes ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Feed Types
                </h2>
                <div
                  ref={refs.feedtypes}
                  style={{
                    maxHeight: expandedSections.feedtypes ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.feedtypes ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <FeedTypes/>
                </div>
              </div>
            </Element>
            <Element name="filedetails">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('filedetails')}
                  id="filedetails"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.filedetails ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  File Types
                </h2>
                <div
                  ref={refs.filedetails}
                  style={{
                    maxHeight: expandedSections.filedetails ? '800px' : '0px',
                  }}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.filedetails ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <FileDetails/>
                </div>
              </div>
            </Element>
            <Element name="tos">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('tos')}
                  id="tos"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.tos ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Terms Settings
                </h2>
                <div
                  ref={refs.tos}
                  style={{maxHeight: expandedSections.tos ? '800px' : '0px'}}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.tos ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <Agreement/>
                </div>
              </div>
            </Element>
            <Element name="roster">
              <div className="border-b-[1px] border-gray-200 dark:border-stone-600">
                <h2
                  onClick={() => toggleSection('roster')}
                  id="tos"
                  className="w-full select-none pb-3 pl-12 pt-3 text-xl font-semibold text-gray-800 dark:text-gray-200"
                >
                  <button className="mr-1 mt-2 text-sm text-stone-800 dark:text-stone-300">
                    {expandedSections.roster ? (
                      <FiChevronDown size={16}/>
                    ) : (
                      <FiChevronRight size={16}/>
                    )}
                  </button>
                  Roster Sync
                </h2>
                <div
                  ref={refs.roster}
                  style={{maxHeight: expandedSections.roster ? '800px' : '0px'}}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${expandedSections.roster ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
                >
                  <Roster/>
                </div>
              </div>
            </Element>
          </>
        )}
        <div className="pb-[500px]"></div>
      </SettingsShell>
    </div>
  );
}
